import { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts';
import { Monitor, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { gpuData } from '@/data/gpuData';
import { gamesBenchmarks, Resolution, resolutionLabels, getFPSForResolution } from '@/data/benchmarkData';
import { cn } from '@/lib/utils';

const brandColors: Record<string, string> = {
  nvidia: 'hsl(83, 100%, 42%)',
  amd: 'hsl(0, 80%, 55%)',
  intel: 'hsl(210, 100%, 50%)',
};

export const BenchmarkCharts = () => {
  const [selectedGame, setSelectedGame] = useState(gamesBenchmarks[0]);
  const [resolution, setResolution] = useState<Resolution>('1440p');
  const [showTopN, setShowTopN] = useState(15);

  const chartData = useMemo(() => {
    const data = selectedGame.benchmarks
      .map((benchmark) => {
        const gpu = gpuData.find((g) => g.id === benchmark.gpuId);
        if (!gpu) return null;
        
        return {
          name: gpu.name,
          fps: getFPSForResolution(benchmark, resolution),
          brand: gpu.brand,
          fullName: gpu.name,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b!.fps - a!.fps)
      .slice(0, showTopN);

    return data as { name: string; fps: number; brand: string; fullName: string }[];
  }, [selectedGame, resolution, showTopN]);

  const maxFPS = Math.max(...chartData.map((d) => d.fps));

  // FPS thresholds for reference lines
  const fpsThresholds = [
    { fps: 60, label: '60 FPS', color: 'hsl(145, 60%, 45%)' },
    { fps: 144, label: '144 FPS', color: 'hsl(210, 80%, 55%)' },
    { fps: 240, label: '240 FPS', color: 'hsl(270, 60%, 55%)' },
  ];

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-card border shadow-lg rounded-lg p-3">
          <p className="font-semibold text-sm">{data.fullName}</p>
          <p className="text-2xl font-bold" style={{ color: brandColors[data.brand] }}>
            {data.fps} FPS
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {resolutionLabels[resolution]}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <section id="benchmarks" className="py-16 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Game Benchmark Comparison
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Compare GPU performance across popular games at different resolutions. 
              Data represents average FPS at high/ultra settings.
            </p>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
            {/* Game Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2 bg-card min-w-[200px] justify-between">
                  <span className="flex items-center gap-2">
                    <span>{selectedGame.icon}</span>
                    <span>{selectedGame.name}</span>
                  </span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[200px] bg-card border shadow-lg">
                {gamesBenchmarks.map((game) => (
                  <DropdownMenuItem
                    key={game.id}
                    onClick={() => setSelectedGame(game)}
                    className={cn(
                      'flex items-center gap-2 cursor-pointer',
                      selectedGame.id === game.id && 'bg-accent'
                    )}
                  >
                    <span>{game.icon}</span>
                    <span>{game.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Resolution Toggle */}
            <ToggleGroup 
              type="single" 
              value={resolution} 
              onValueChange={(v) => v && setResolution(v as Resolution)}
              className="bg-card border rounded-lg p-1"
              aria-label="Select resolution"
            >
              <ToggleGroupItem value="1080p" className="gap-1 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" aria-label="1080p resolution">
                <Monitor className="w-3 h-3" />
                1080p
              </ToggleGroupItem>
              <ToggleGroupItem value="1440p" className="gap-1 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" aria-label="1440p resolution">
                <Monitor className="w-4 h-4" />
                1440p
              </ToggleGroupItem>
              <ToggleGroupItem value="4k" className="gap-1 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" aria-label="4K resolution">
                <Monitor className="w-5 h-5" />
                4K
              </ToggleGroupItem>
            </ToggleGroup>

            {/* Top N Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2 bg-card">
                  Top {showTopN} GPUs
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-card border shadow-lg">
                {[10, 15, 20, 27].map((n) => (
                  <DropdownMenuItem
                    key={n}
                    onClick={() => setShowTopN(n)}
                    className={cn(
                      'cursor-pointer',
                      showTopN === n && 'bg-accent'
                    )}
                  >
                    Top {n} GPUs
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Chart */}
          <div className="bg-card border rounded-xl p-6 shadow-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-lg">
                  {selectedGame.name} - {resolutionLabels[resolution]}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {selectedGame.genre} • Released {selectedGame.releaseYear}
                </p>
              </div>
              
              {/* Legend */}
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: brandColors.nvidia }} />
                  <span>NVIDIA</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: brandColors.amd }} />
                  <span>AMD</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: brandColors.intel }} />
                  <span>Intel</span>
                </div>
              </div>
            </div>

            <div className="h-[500px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  layout="vertical"
                  margin={{ top: 10, right: 30, left: 100, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="hsl(var(--border))" />
                  <XAxis
                    type="number"
                    domain={[0, Math.ceil(maxFPS / 50) * 50]}
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                    tickLine={{ stroke: 'hsl(var(--border))' }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fill: 'hsl(var(--foreground))', fontSize: 12 }}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                    tickLine={false}
                    width={95}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(var(--muted) / 0.3)' }} />
                  
                  {/* FPS Reference Lines */}
                  {fpsThresholds
                    .filter((t) => t.fps <= maxFPS * 1.1)
                    .map((threshold) => (
                      <line
                        key={threshold.fps}
                        x1={threshold.fps}
                        x2={threshold.fps}
                        y1={0}
                        y2="100%"
                        stroke={threshold.color}
                        strokeDasharray="4 4"
                        strokeWidth={1}
                      />
                    ))}

                  <Bar dataKey="fps" radius={[0, 4, 4, 0]} barSize={20}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={brandColors[entry.brand]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* FPS Threshold Legend */}
            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t">
              {fpsThresholds
                .filter((t) => t.fps <= maxFPS * 1.1)
                .map((threshold) => (
                  <div key={threshold.fps} className="flex items-center gap-2 text-xs">
                    <div
                      className="w-4 h-0.5"
                      style={{ backgroundColor: threshold.color, borderStyle: 'dashed' }}
                    />
                    <span className="text-muted-foreground">{threshold.label}</span>
                  </div>
                ))}
            </div>
          </div>

          {/* Info Note */}
          <p className="text-xs text-muted-foreground text-center mt-4">
            Benchmarks represent typical performance at high/ultra presets. Actual results may vary based on system configuration, drivers, and game updates.
          </p>
        </div>
      </div>
    </section>
  );
};
